<template>
	<div class="flow-timeseries-container" :empty="empty" :state="state" :transparent="transparent">
		<div v-if="draggable" class="flow-timeseries-draggable">
			<Icon name="i-arrange" size="small" state="dark" :effects="false" type="filled" />
		</div>
		<div class="flow-timeseries-topbar">
			<div class="flow-timeseries-header">
				{{ header }}
			</div>
			<div class="flow-timeseries-legends">
				<div
					v-for="(sr, srindex) in series"
					:key="srindex + '_time_legend'"
					class="flow-timeseries-legend"
					:disabled="
						chart && type === 'chart' ? chart.data.shown(sr.name).length === 0 : showDisabled
					"
					@mouseout="handleLegendOut()"
					@mouseover="handleLegendHover(sr.name)"
					@click="handleLegendClick(sr.name)"
				>
					<span class="flow-legend-circle" :style="'background-color:' + getSeriesColor(sr)" />
					<span class="flow-legend-label text-ellipsis">{{ sr.label ? sr.label : sr.name }}</span>
				</div>
			</div>
		</div>
		<Loader v-if="loading" position="absolute" />
		<div v-show="type === 'chart'" ref="chartdom" class="flow-timeseries-chart" />
		<div
			v-show="type === 'number' && !loading"
			ref="numberdom"
			class="flow-timeseries-number"
			:style="{ fontSize: numberFontSize.bignumber + 'px' }"
		>
			<template v-for="(activeSeries, idx) in series">
				<div
					v-if="activeSeriesIndex === idx"
					:key="activeSeries.name"
					class="flow-timeseries-number-stat"
					:data-series-count="series.length"
					:set="((dev = getDeviation(activeSeries)), (value = getLatestValue(activeSeries)))"
				>
					<div class="flow-timeseries-number-latest" :style="{ color: activeSeries.color }">
						{{ value.y }}
					</div>
					<div
						class="flow-timeseries-number-symbol"
						:style="{ color: activeSeries.color, fontSize: numberFontSize.symbol + 'px' }"
					>
						{{ value.unit }}
					</div>
					<div
						v-if="dev.value !== 0"
						class="flow-timeseries-number-deviation"
						:style="{ fontSize: numberFontSize.deviation + 'px' }"
					>
						<span class="flow-ts-n-d-symbol">
							<Icon
								v-if="dev.type !== 'neutral'"
								name="filled-arrow"
								:rotate="dev.type === 'negative' ? 0 : 180"
								size="small"
								type="filled"
								:effects="false"
								:state="getDeviationIconState(dev.type)"
							/>
						</span>
						{{ dev.value }}
					</div>
				</div>
				<div
					v-if="activeSeriesIndex === idx"
					:key="activeSeries.name + '_label'"
					:data-series-count="series.length"
					class="flow-timeseries-label"
				>
					{{ activeSeries.name }}
				</div>
			</template>
		</div>
		<AlarmCircle
			v-if="state === 'error' || state === 'warning'"
			class="flow-timeseries-alarm-circle"
			:type="state"
		>
			<Icon name="bell-ring" state="light" :effects="false" @click="handleAlarmClick($event)" />
		</AlarmCircle>
	</div>
</template>
<script>
import bb from "billboard.js";
import Vue, { defineComponent } from "vue";
import TimeseriesTooltip from "./TimeseriesTooltip";
import AlarmCircle from "./../../AlarmCircle/AlarmCircle";
import Icon from "./../../Icon/Icon";
import UnitConverter from "./../..//utils/UnitConverter";

import Loader from "./../../Loader/Loader";
import TimeseriesGetters from "./TimeseriesGetters";

import "billboard.js/dist/billboard.min.css";

export default defineComponent({
	name: "Timeseries",
	components: {
		// eslint-disable-next-line vue/no-unused-components
		TimeseriesTooltip,
		AlarmCircle,
		Icon,
		Loader
	},
	props: {
		/**
		 * Header of chart
		 */
		header: {
			type: String,
			default: "Timeseries Chart"
		},
		/**
		 * <code>
		 * <pre>
		 * [
		 * 	{
		 * 		name : String  E.g. "SeriesName" | "MetricName",
		 * 		color : String E.g. "#000",
		 * 		thresholdLines:[
		 * 			{
		 * 				type : String E.g. "error" | "warning" | "success",
		 * 				value : Number E.g. Y axis value,
		 * 				text : String E.g. "> 50",
		 * 				position : String E.g. "start" | "middle" | "end"
		 * 			} , ...
		 * 		],
		 * 		points:[
		 * 			{ x : Number, y : Number}, ...
		 * 		],
		 * 		yAxisUnit:	String E.g. "percentage" | "number" | "bytes" | "time",
		 * 		yAxisName : String E.g. "y" | "y2",
		 * 		type : String  "area" | "areaLineRange" | "areaSpline" |
		 * 		"areaSplineRange" | "areaStep" | "bar" |
		 * 		"bubble" | "line" | "scatter" | "spline" | "step"
		 * 	} , ...
		 * ]
		 * </pre>
		 * </code>
		 */
		series: {
			type: Array,
			default: () => []
		},
		/**
		 * Vertical lines to display events it accepts milliseconds
		 */
		eventLines: {
			type: Array,
			default: () => []
		},
		/**
		 * true | false
		 */
		groupedTooltip: {
			type: Boolean,
			default: true
		},
		/**
		 * true | false
		 */
		linkedTooltip: {
			type: Boolean,
			default: true
		},
		/**
		 * 'default' | 'error' | 'warning'
		 */
		state: {
			type: String,
			default: "default",
			validator(value) {
				return ["error", "default", "warning"].includes(value);
			}
		},
		/**
		 * 'chart' | 'number'
		 */
		type: {
			type: String,
			default: "chart",
			validator(value) {
				return ["chart", "number"].includes(value);
			}
		},
		/**
		 * true | false <br/>if true then positive deviation will be showed in red color
		 */
		invertDeviationColor: {
			type: Boolean,
			default: false
		},
		enableDeviationColor: {
			type: Boolean,
			default: false
		},
		transparent: {
			type: Boolean,
			default: false
		},
		noDataMessage: {
			type: String,
			default: "No Data"
		},
		loading: {
			type: Boolean,
			default: false
		},
		noDigitsAfterDeviationDecimal: {
			type: Number,
			default: 1
		},
		/**
		 * minimun value on X <br/>
		 * &lt;number&gt; | { fit : &lt;boolean&gt;, value : &lt;number&gt;}
		 */
		xMin: {
			type: [Number, Object],
			default: null
		},
		/**
		 * maximum value on X <br/>
		 * &lt;number&gt; | { fit : &lt;boolean&gt;, value : &lt;number&gt;}
		 */
		xMax: {
			type: [Number, Object],
			default: null
		},
		/**
		 * array of xValues
		 */
		xTickValues: {
			type: Array,
			default: () => null
		},
		draggable: {
			type: Boolean,
			default: false
		},
		xTickPosition: {
			type: Object,
			default: () => {
				return { x: 0, y: 0 };
			}
		},
		/**
		 * true | false
		 */
		connectNull: {
			type: Boolean,
			default: true
		},
		/**
		 * Used for bar width in bar chart
		 */
		barWidthRatio: {
			type: Number,
			default: 0.3
		}
	},
	data() {
		return {
			/**
			 * id of DOM element
			 */
			id: "Timeseries_Chart",
			/**
			 * hold bb chart object
			 */
			chart: null,
			/**
			 * last x axis point plotted
			 */
			lastX: {},
			tickFormat: "%I:%M",
			showY2: false,
			yAxisUnits: { y: "COUNT._", y2: "COUNT._" },
			activeSeriesIndex: 0,
			activeSeriesInterval: null,
			numberFontSize: {
				bignumber: 100,
				symbol: 30,
				deviation: 50
			},
			noOfTicksRemoved: 0, // hold value to remove no of x ticks while updating chart
			tickGreaterThan: 0, // x axis tick must be greater than this
			tickInterval: 0 // constant tick interval based on chart width
		};
	},
	computed: {
		showDisabled() {
			return this.type === "chart";
		},
		empty() {
			if (this.series && this.series.length === 1 && !this.series[0].points) {
				return true;
			} else if (
				this.series &&
				this.series.every(sr => {
					return !(sr.points && sr.points.length > 0);
				})
			) {
				return true;
			} else {
				return false;
			}
		},
		maxValues() {
			const maxValue = { y: { value: 0 }, y2: { value: 0 } };
			if (this.series) {
				this.series.forEach(sr => {
					if (sr.points) {
						const srValues = sr.points.map(p => {
							return p.y;
						}); //this.chart.data.values(sr.name);

						const topValue = Math.max(...srValues);
						// console.log(topValue);
						const yAxisName = sr.yAxisName ? sr.yAxisName : "y";
						if (topValue >= maxValue[yAxisName].value) {
							maxValue[yAxisName].value = topValue;
							const yUnit = sr.yAxisUnit ? sr.yAxisUnit : "COUNT._";
							maxValue[yAxisName].unit = UnitConverter.normalize(topValue, yUnit).unit;
						}
					}
				});
			}
			return maxValue;
		}
	},
	watch: {
		/**
		 * watch for series update
		 */
		series: {
			handler() {
				//	console.log("[Timeseries] series data updated...", newseries);
				if (this.type === "chart" && !this.loading) {
					this.updateChartOrPlot();
				}
			},
			deep: true
		},
		/**
		 * Watching for event lines
		 */
		eventLines: {
			handler() {
				//	console.log("[Timeseries] series data updated...", newseries);
				if (this.chart && this.type === "chart") {
					this.chart.xgrids(this.getEventLines().lines);
				}
			}
		},
		/**
		 * Wathcing if type is changed
		 */
		type: {
			handler() {
				this.init();
			}
		},
		loading: {
			handler() {
				this.init();
			}
		},
		xTickValues: {
			handler() {
				if (this.chart && this.xTickValues) {
					this.chart.config("axis.x.tick.values", this.xTickValues);
				}
			}
		},
		xMin: {
			handler() {
				if (this.chart && this.xMin) {
					this.chart.config("axis.x.min", this.xMin);
				}
			}
		},
		xMax: {
			handler() {
				if (this.chart && this.xMax) {
					this.chart.config("axis.x.max", this.xMax);
				}
			}
		}
	},
	mounted() {
		/**
		 * Initializing Timeseries
		 */
		this.init();
	},
	/**
	 * Destroy chart instance and clear timeout Before destroying this component
	 */
	beforeDestroy() {
		/**
		 * Detroy chart whenever component is destroyed
		 */
		if (this.chart && this.type === "chart") {
			//console.log("[Timeseries] Destroying...");
			this.chart.destroy();
		}

		if (this.activeSeriesInterval) {
			clearInterval(this.activeSeriesInterval);
		}
	},
	/**
	 * Generate id and register tooltip component Before mount
	 */
	beforeMount() {
		/**
		 * Generate Id and assign
		 */
		this.id = this.generateChartId();
		//	console.log(this.id);
		/**
		 * Register custom tolltip component
		 */
		this.tooltip = Vue.extend(TimeseriesTooltip);
	},
	methods: {
		...TimeseriesGetters,
		/**
		 * Inittialize timeseries
		 */
		init() {
			if (this.type === "chart" && this.loading == false) {
				//	console.log("[Timeseries] Initializing...", this.series);
				/**
				 * Plotting chart
				 */
				this.plot();

				/**
				 * Resize chart whenever window size changes
				 */
				window.addEventListener("resize", () => {
					this.autoSize();
				});
			}

			if (this.type === "number" && this.loading == false) {
				if (this.activeSeriesInterval) {
					clearInterval(this.activeSeriesInterval);
				}
				this.activeSeriesInterval = setInterval(() => {
					// console.log("In setInterval");
					if (this.activeSeriesIndex < this.series.length - 1) {
						this.activeSeriesIndex++;
					} else {
						this.activeSeriesIndex = 0;
					}
				}, 3000);
				this.$nextTick(() => {
					setTimeout(() => {
						this.calcNumberFontSize();
					}, 100);
				});
			}
		},

		/**
		 * Assign latest size of container
		 */
		autoSize() {
			this.$nextTick(() => {
				if (this.chart) {
					this.tickInterval = 0;
					const newSize = this.getSize();

					this.updateChartConfig();
					this.chart.resize({
						width: newSize.width,
						height: newSize.height
					});
				}
				this.calcNumberFontSize();
			});
		},
		/**
		 * Calclulate Number font size for type="number"
		 */
		calcNumberFontSize() {
			if (this.$refs.numberdom) {
				const height = this.$refs.numberdom.offsetHeight;
				const width = this.$refs.numberdom.offsetWidth;
				const clacAgainst = height < width ? height : width;

				const bignumbersize = clacAgainst / 2.5;
				this.numberFontSize.bignumber = bignumbersize;
				this.numberFontSize.symbol = bignumbersize * 0.3;
				this.numberFontSize.deviation = bignumbersize * 0.5;
			}
		},
		/**
		 * Update or plot chart based on chart object
		 */
		updateChartOrPlot() {
			if (this.chart) {
				this.updateChart();
			} else {
				this.plot();
			}
		},
		/**
		 * Update chart with new data points
		 */
		updateChart() {
			/**
			 * Initializing blank objetcs
			 */
			const newColumnValues = [];
			const xAxisSet = new Set();
			/**
			 * Iterating over series to find new values
			 */
			this.series.forEach(sr => {
				const lastX = this.lastX[sr.name];
				// find latest values by comparing last point
				const newPoints = sr.points.filter(pt => pt.x > lastX);

				//	console.log("[Timeseries] plotting new ponits in ", sr.name, newPoints);
				// initializing array

				const x_data_name = `${sr.name}_x`;
				const yColumn = [sr.name];
				const xColumn = [x_data_name];

				newPoints.forEach(point => {
					xAxisSet.add(point.x);
					xColumn.push(point.x);
					yColumn.push(point.y);

					this.lastX[sr.name] = point.x;
				});

				// newColumnValues.push(xColumn);
				newColumnValues.push(yColumn);
			});
			/**
			 * adding new values of x axis
			 */
			const xAxis = ["X_AXIS", ...xAxisSet];
			newColumnValues.push(xAxis);

			/**
			 * Adding new value to chart
			 */
			this.chart.flow({
				columns: newColumnValues,
				duration: 600,
				done: () => {
					/**
					 * Remove older values from x axis
					 */
					this.noOfTicksRemoved += xAxis.length - 1;
					if (this.chart.x()[this.series[0].name]) {
						const currentpoints = [...this.chart.x()[this.series[0].name]];
						for (let idx = 0; idx < this.noOfTicksRemoved; idx++) {
							currentpoints.shift();
						}
						// // this.chart.x(currentpoints);

						// this.chart.config("axis.x.min", currentpoints[0]);
						// this.ticksToRemove += noOfValuesToRemove;
						// console.log(currentpoints[0].getTime());
						if (currentpoints[0]) {
							this.tickGreaterThan = currentpoints[0].getTime();
						}
					}
					this.updateChartConfig();
					this.chart.xgrids(this.getEventLines().lines); // plot event lines if any
				}
			});

			//	console.log("[Timeseries] Chart Updated...");
		},
		updateChartConfig() {
			this.chart.config(
				"axis.x.tick.values",
				this.xTickValues ? this.xTickValues : this.getTickValues()
			);

			const xMinMax = this.getXminAndXmax();
			if (xMinMax.min) {
				this.chart.config("axis.x.min", xMinMax.min, true);
			}
			if (xMinMax.max) {
				this.chart.config("axis.x.max", xMinMax.max, true);
			}
			this.chart.config("axis.x.tick.culling.max", this.getXMaxCulling());
			this.chart.config("axis.y.tick.culling.max", this.getYTickCount());
			this.chart.config("axis.y2.tick.culling.max", this.getYTickCount());

			let yMax;
			this.series.forEach(sr => {
				if (sr.yAxisName !== "y2") {
					const maxThreshold =
						sr.thresholdLines && sr.thresholdLines.length > 0
							? Math.max(...sr.thresholdLines.map(line => line.value))
							: 0;

					const maxPoint = (max, point) => {
						return (max = point.y > max ? point.y : max);
					};
					const maxValue = sr.points ? sr.points.reduce(maxPoint, 0) : 0;

					const srYMax = Math.max(maxThreshold, maxValue);

					if (srYMax > yMax || yMax === undefined) {
						yMax = srYMax;
					}
				}
			});
			if (yMax && yMax !== 0) {
				this.chart.config("axis.y.max", yMax, true);
			}

			let yMin = 0;
			this.series.forEach(sr => {
				if (sr.yAxisName !== "y2") {
					const minThreshold =
						sr.thresholdLines && sr.thresholdLines.length > 0
							? Math.min(...sr.thresholdLines.map(line => line.value))
							: 0;
					const minPoint = (min, point) => {
						return (min = point.y < min ? point.y : min);
					};
					const minValue = sr.points ? sr.points.reduce(minPoint, 0) : 0;

					const srYMin = Math.min(minThreshold, minValue);

					if (srYMin < yMin || yMin === undefined) {
						yMin = srYMin;
					}
				}
			});
			if (yMin !== null) {
				this.chart.config("axis.y.min", yMin, true);
				this.chart.config("axis.y2.min", yMin, true);
			}
		},
		plot() {
			if (this.series.length > 0) {
				/**
				 * to debug existing chart
				 *
				 */

				//	console.log("[Timeseries] Creating New Chart...");
				this.chart = bb.generate({
					data: {
						...this.getData(),
						empty: {
							label: {
								text: this.noDataMessage
							}
						}
					},
					axis: {
						y2: {
							show: this.showY2,
							...this.getYAxisConfig("y2")
						},
						x: {
							type: "timeseries",
							tick: {
								tooltip: true,
								centered: true,
								format: this.tickFormat,
								values: this.xTickValues ? this.xTickValues : this.getTickValues(),
								culling: {
									max: this.getXMaxCulling()
								},
								fit: true,
								text: {
									position: this.xTickPosition
								},
								outer: false
							},
							...this.getXminAndXmax()
						},
						y: {
							...this.getYAxisConfig("y")
						}
					},
					bar: {
						width: {
							ratio: this.barWidthRatio,
							max: 20
						}
					},
					line: {
						connectNull: this.connectNull
					},
					bindto: this.$refs.chartdom,
					// point: {
					// 	show: false
					// },
					size: this.getSize(),
					legend: {
						show: false
					},
					point: {
						show: true,
						r: 2,
						sensitivity: 1000,
						focus: {
							expand: {
								r: 2
							}
							// only: true
						}
					},
					grid: {
						x: {
							show: true,
							...this.getEventLines()
						},
						y: {
							show: true,
							...this.getThresholdLines()
						},
						focus: {
							y: true,
							show: true,
							edge: false
						}
					},
					tooltip: {
						...this.getTooltip()
					},
					transition: {
						duration: 0
					}
				});
				// fix : to render or aquire container width
				setTimeout(() => {
					this.autoSize();
				}, 100);

				//	console.log("Available Charts : ", bb.instance);
			}
		},

		/**
		 * Generate random ID for each Instance
		 */
		generateChartId() {
			let id = "";
			const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz";
			const charsLength = chars.length;
			for (let i = 0; i < 10; i++) {
				id += chars.charAt(Math.floor(Math.random() * charsLength));
			}
			return id;
		},
		/**
		 * handle Alarm click
		 */
		handleAlarmClick(e) {
			/**
			 * on alarm icon click
			 *
			 * @event alarmclick
			 */
			this.$emit("alarmclick", e);
		},

		handleLegendHover(seriesname) {
			if (this.chart && seriesname) {
				this.chart.focus(seriesname);
			}
		},
		handleLegendOut() {
			if (this.chart) {
				this.chart.revert();
			}
		},
		handleLegendClick(seriesname) {
			if (this.chart && seriesname) {
				this.chart.toggle(seriesname);
			}
		}
	}
});
</script>
<style lang="scss">
$state-colors: (
	"error": var(--dashboard-danger-gradient),
	"warning": var(--dashboard-warning-gradient)
);

div.flow-timeseries-container {
	background-color: var(--dashboard-element-light) !important;
	height: 100%;
	border-radius: 4px;
	position: relative;
	&[transparent] {
		background-color: transparent !important;
	}

	// &[empty] {
	// 	padding: 16px 16px 0px 16px;
	// }

	// display: flex;
	// flex-direction: column;

	@each $state, $color in $state-colors {
		&[state="#{$state}"] {
			background: $color;
		}
	}
	> div.flow-timeseries-topbar {
		display: grid;
		grid-template-columns: 1fr 36px;
		grid-template-rows: 24px 24px;
		max-height: 48px;
		> div.flow-timeseries-legends {
			grid-area: 2 / 1 / end / 2;
			display: flex;
			overflow-x: auto;
			overflow-y: hidden;
			align-items: center;
			padding: 6px 12px 0px 12px;
			max-height: 24px;
			// flex: 0 0 auto;

			> div.flow-timeseries-legend {
				margin-right: 8px;
				display: flex;
				align-items: center;
				cursor: pointer;

				&[disabled] {
					opacity: 0.5;
					> span.flow-legend-circle {
						background-color: var(--dashboard-font-light) !important;
					}
				}
				> span.flow-legend-circle {
					width: 8px;
					height: 8px;
					border-radius: 4px;
					margin-right: 6px;
				}

				> span.flow-legend-label {
					font: 12px var(--body-font);
					line-height: 12px;
					color: var(--dashboard-font-light);

					max-width: 180px;
				}

				&:hover {
					> span.flow-legend-label {
						color: var(--dashboard-font-dark);
					}
				}
			}
		}

		> div.flow-timeseries-header {
			grid-area: 1 / 1 / 2 / 2;
			white-space: nowrap;
			overflow: hidden;
			text-overflow: ellipsis;
			font-size: 12px;
			line-height: 12px;
			font-weight: normal;
			color: var(--dashboard-font-dark);
			padding: 12px 12px 0px 12px;
			// flex: 0 0 auto;
			display: flex;
			align-items: center;
		}
	}

	.bb-title {
		white-space: nowrap;
		overflow: hidden;
		text-overflow: ellipsis;
		font-size: 14px;
		font-weight: normal;
		color: var(--dashboard-font);
		fill: var(--dashboard-font);
		stroke: none !important;
		font: 14px var(--body-font);
	}
	&[empty] {
		> div.flow-timeseries-chart {
			padding: 12px;
			height: calc(100% - 60px);
			text {
				transform: translateX(15px);
			}
		}
	}
	> div.flow-timeseries-chart {
		margin: 8px;
		// margin-bottom: 12px;
		height: 100%;
		svg {
			font: 10px var(--body-font);
			overflow: visible !important;
		}

		.bb-axis line,
		.bb-axis path.domain {
			fill: none;
			stroke: transparent;
		}

		.bb-grid line.bb-xgrid,
		.bb-grid line.bb-ygrid {
			stroke: var(--dashboard-element);
			fill: none;
		}

		.bb-grid line.bb-xgrid-focus,
		.bb-grid line.bb-ygrid-focus {
			stroke: yellow;
			stroke-width: 1px;
		}

		text {
			transition: stroke 0.3s linear;
			stroke: var(--dashboard-font-light);
		}

		.bb-chart-lines {
			.bb-chart-line {
				.bb-lines {
					.bb-line {
						stroke-width: 2px;
					}
				}

				.bb-circles {
					.bb-circle {
						display: none;
						transition: opacity 0.3s linear;
					}
					.bb-circle:only-child {
						display: block;
					}
				}
			}
		}

		.flow-timeseries {
			&-warning,
			&-error,
			&-success {
				cursor: pointer;
				&:hover {
					> line {
						stroke-width: 2px;
					}
					> text {
						font-weight: bold;
						font-size: 14px;
					}
				}
			}
			&-warning {
				> line {
					stroke: var(--dashboard-warning);
					fill: var(--dashboard-warning);
				}
				> text {
					stroke: var(--dashboard-warning);
					fill: var(--dashboard-warning);
				}
			}
			&-error {
				cursor: pointer;
				> line {
					stroke: var(--dashboard-danger);
					fill: var(--dashboard-danger);
				}
				> text {
					stroke: var(--dashboard-danger);
					fill: var(--dashboard-danger);
				}
			}
			&-success {
				> line {
					stroke: var(--dashboard-success);
					fill: var(--dashboard-success);
				}
				> text {
					stroke: var(--dashboard-success);
					fill: var(--dashboard-success);
				}
			}
		}

		.flow-timeseries-event-line {
			> line {
				stroke: yellow;
				stroke-width: 2px;
			}
		}

		.hover-focus-point {
			> line {
				stroke: red;
				stroke-width: 2px;
			}
		}

		&:hover {
			.bb-chart-lines {
				.bb-chart-line {
					// .bb-lines {
					// 	.bb-line {
					// 		stroke-width: 2px;
					// 	}
					// }

					.bb-circles {
						.bb-circle {
							display: block;
						}
					}
				}
			}

			.tick text {
				stroke: var(--dashboard-font);
			}

			.bb-chart {
				cursor: pointer;
			}
		}

		.bb-text.bb-empty {
			fill: var(--dashboard-font-light);
			font-size: 14px;
		}
	}

	.flow-timeseries-alarm-circle {
		position: absolute;
		bottom: 0;

		> span.alarm-content {
			display: inline-flex;
			align-items: center;
			justify-self: center;

			div.icon-container > div.icon svg {
				height: 20px;
				width: 20px;
			}
		}
	}
	// div.bb-tooltip-container {
	// 	table.bb-tooltip {
	// 		background-color: black;
	// 		min-width: 300px;
	// 		// opacity: 0.6;
	// 		tr {
	// 			border: none;
	// 			td,
	// 			th {
	// 				background-color: black;
	// 				font: 12px var(--body-font);
	// 				color: var(--dashboard-font-dark);
	// 				border: none;
	// 			}
	// 		}
	// 	}
	// }

	div.flow-timeseries-number {
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		height: calc(100% - 60px);
		flex: 1 0 auto;

		> div.flow-timeseries-number-stat {
			display: flex;
			justify-content: center;
			align-items: flex-end;
			&[data-series-count="1"] {
				animation: unset;
			}
			animation: tm-fades 3s forwards;

			> div.flow-timeseries-number-latest {
				font-family: var(--number-font);
				font-size: inherit;
				color: var(--dashboard-font-dark);
				line-height: 77%;
				margin-right: 12px;
				font-weight: 300;
			}

			> div.flow-timeseries-number-symbol {
				font-family: var(--number-font);
				color: var(--dashboard-font-dark);
				line-height: 77%;
				font-weight: 300;
				// margin-left: 2px;
				margin-right: 12px;
			}
			> div.flow-timeseries-number-deviation {
				font-family: var(--number-font);
				color: var(--dashboard-font-dark);
				line-height: 77%;
				margin-left: 12px;
				font-weight: 300;

				display: flex;
				flex-direction: column;
			}
		}

		> div.flow-timeseries-label {
			font-size: 16px;
			color: var(--dashboard-font-dark);
			margin-top: 32px;
			font-weight: 300;
			text-align: center;
			display: -webkit-box;
			max-width: calc(100% - 24px);
			-webkit-line-clamp: 3;
			-webkit-box-orient: vertical;
			overflow: hidden;
			&[data-series-count="1"] {
				animation: unset;
			}
			animation: tm-fades 3s forwards;
		}

		@keyframes tm-fades {
			0% {
				opacity: 0;
			}
			30% {
				opacity: 1;
			}
			70% {
				opacity: 1;
			}
			100% {
				opacity: 0;
			}
		}
	}

	div.flow-timeseries-draggable {
		border-radius: 8px;
		position: absolute;
		width: 100%;
		height: 100%;
		display: flex;
		align-items: center;
		justify-content: center;
		background: rgba(0, 0, 0, 0.3);
		z-index: 3;
		cursor: move;
	}
}

// /* Enter and leave animations can use different */
// /* durations and timing functions.              */
// .tm-fade-enter-active {
// 	transition: opacity 1s ease;
// }
// .tm-fade-leave-active {
// 	transition: all 2s cubic-bezier(1, 0.5, 0.8, 1);
// }
// .tm-fade-enter, .tm-fade-leave-to
// /* .slide-fade-leave-active below version 2.1.8 */ {
// 	// transform: translateX(10px);
// 	opacity: 0;
// }
</style>
